/**
 * Cartography — the baked ink treatment (labels, roads, water) written into the
 * live style as real MapLibre paint properties.
 *
 * The OpenFreeMap "dark" style is near-monochrome by construction: every place
 * label ships at rgb(101,101,101) over an rgb(12,12,12) background — 3.4:1,
 * under the WCAG AA 4.5:1 floor — water fills at rgb(27,27,29) against land at
 * rgb(12,12,12), and minor roads at #181818. On a visual whose entire job is to
 * say WHERE a place is, the names are the payload, and they were unreadable.
 *
 * The first mitigation was a `filter: saturate() brightness()` over the whole
 * canvas, which is a blunt instrument: it tints the pins, the popups and the
 * accent along with the basemap, and it cannot tell a label from a motorway.
 * This module replaces it by restyling exactly the layers that carry the
 * problem, leaving everything drawn on top of the canvas untouched.
 *
 * Every ink axis is retired and baked (locked in the pillar-widgets panel):
 * Sovereign labels — country and continent names in the product accent, every
 * other name white, both on a near-black halo; Whisper roads — hairlines an
 * order of magnitude under emitted-light greys, structure for the labels and
 * pins to stand on; Petrol water — a low-chroma blue-leaning edge over a fill
 * a few points off the land, so the coastline reads as depth rather than tint.
 *
 * Kept React-free and DOM-free on purpose, and the map is typed through
 * `typeof import('maplibre-gl')` the way `MapCanvas` and `map-worker` do it, so
 * nothing here forces the library (which touches `window` at module scope) to
 * be evaluated. The module can therefore be reasoned about without a browser.
 */

type MapLibreModule = typeof import('maplibre-gl');
type MapLibreMapInstance = InstanceType<MapLibreModule['Map']>;
type StyleLayer = import('maplibre-gl').LayerSpecification;
/** Every paint property in the style spec, and the union of their value types.
 *  Indexing the flat spec record (rather than one layer type's paint block) is
 *  what lets ONE writer serve the label, road and water treatments alike. */
type PaintProperties = import('maplibre-gl').AllPaintProperties;
type PaintPropertyName = keyof PaintProperties;
/** A style expression — the value form a paint property takes when it is
 *  evaluated PER FEATURE rather than held constant across a layer. Reachable
 *  through `maplibre-gl` because it re-exports the style-spec types wholesale
 *  (`export type * from '@maplibre/maplibre-gl-style-spec'`), so this needs no
 *  second dependency. */
type StyleExpression = import('maplibre-gl').ExpressionSpecification;

/**
 * OpenMapTiles source-layer names.
 *
 * Classification runs on `source-layer` + layer `type`, never on a hardcoded
 * list of vendor layer ids. Layer ids are the style author's free choice and
 * get renamed between style releases (OpenFreeMap's own `highway_major_inner`
 * has no counterpart in MapTiler's or Stadia's OMT styles), whereas the
 * OpenMapTiles schema these styles all consume is versioned and stable: the
 * road network is `transportation` whoever drew it, and water is `water` /
 * `waterway`. Id-substring matching survives only as a fallback for the one
 * case source-layer cannot answer — a layer drawn from a non-vector source, or
 * a style built on a different schema.
 */
const OMT_TRANSPORTATION_SOURCE_LAYER = 'transportation';
const OMT_WATER_SOURCE_LAYER = 'water';
const OMT_WATERWAY_SOURCE_LAYER = 'waterway';

/**
 * The `place` source-layer's `class` field, and the two values the Sovereign
 * label treatment reads as nation-scale.
 *
 * Country names and city names routinely share ONE layer in these styles — the
 * style author splits them by zoom and text-size, not by layer — so no
 * per-layer colour can tell them apart. `class` is the OpenMapTiles field that
 * can: `continent`, `country`, `state`, `city`, `town`, `village`, `suburb`,
 * `neighbourhood`. Reading it per feature is the only place the split exists.
 *
 * `continent` rides WITH `country` deliberately. It is not a sovereign entity,
 * but it renders only at the world zooms where countries are the sole other
 * labels on screen, and a continent left in the "everything else" white there
 * reads as a name we forgot rather than as a distinction we drew. The accent is
 * marking the world-political frame, not the ISO list.
 */
const OMT_PLACE_CLASS_FIELD = 'class';
const OMT_PLACE_CLASS_COUNTRY = 'country';
const OMT_PLACE_CLASS_CONTINENT = 'continent';

/** Expression operators, spelled once. */
const EXPRESSION_GET = 'get';
const EXPRESSION_EQUALS = '==';
const EXPRESSION_ANY = 'any';
const EXPRESSION_CASE = 'case';

/** Layer-spec discriminants and keys read off the live style. */
const LAYER_TYPE_SYMBOL = 'symbol';
const LAYER_TYPE_LINE = 'line';
const LAYER_TYPE_FILL = 'fill';
const SOURCE_LAYER_PROPERTY = 'source-layer';
const FILTER_PROPERTY = 'filter';
const TEXT_FIELD_PROPERTY = 'text-field';

/** Paint properties the treatment writes. */
const PAINT_TEXT_COLOR = 'text-color';
const PAINT_TEXT_HALO_COLOR = 'text-halo-color';
const PAINT_TEXT_HALO_WIDTH = 'text-halo-width';
const PAINT_LINE_COLOR = 'line-color';
const PAINT_LINE_OPACITY = 'line-opacity';
const PAINT_FILL_COLOR = 'fill-color';
const PAINT_FILL_OUTLINE_COLOR = 'fill-outline-color';
const PAINT_FILL_ANTIALIAS = 'fill-antialias';

/** Fallback-only id hints — consulted when a layer reports no source-layer. */
const ROAD_ID_HINTS = [
  'road',
  'highway',
  'street',
  'motorway',
  'railway',
  'bridge',
  'tunnel',
];
const WATER_ID_HINTS = ['water', 'ocean', 'sea', 'river', 'lake'];

/**
 * OpenMapTiles `transportation.class` values that make a road an arterial.
 *
 * A style does not label its layers "major" and "minor", but it does filter
 * them by class, and the filter travels with the layer in `getStyle()`. So the
 * arterial split reads the layer's OWN filter and looks for these class tokens
 * — which is exactly the distinction the style author already made, rather than
 * a guess from line-width. A filter that negates (`!`, `!=`, `!in`, `none`)
 * cannot be read that literally — `["!=", ["get","class"], "motorway"]` mentions
 * motorway while meaning everything else — so a negated filter disqualifies the
 * layer from the arterial set and it takes the minor treatment. Erring toward
 * minor is the safe direction: the cost is a road that stays grey, not an
 * accent smeared across the whole network.
 */
const ARTERIAL_ROAD_CLASS_NAMES = [
  'motorway',
  'trunk',
  'primary',
  'secondary',
  'tertiary',
];
const ARTERIAL_ROAD_CLASSES = new Set(ARTERIAL_ROAD_CLASS_NAMES);
const FILTER_NEGATION_TOKENS = new Set(['!', '!=', '!in', 'none']);

/** The product accent (Ethereal Glass mint, as in MapExpandedSurface.css). */
const MAP_ACCENT_COLOR = '#66fec1';

/**
 * The country-vs-place split, as one expression evaluated per feature.
 *
 * `['get', 'class']` yields `null` on a feature that has no `class` — a road
 * name, a water name, any label outside the `place` source-layer — and `==`
 * is EQUALITY, not an ordering comparison, so the style-spec's runtime type
 * check does not apply to it: mismatched types compare false rather than
 * throwing (verified against maplibre-gl 6.1.0's `makeComparison`, whose
 * `isOrderComparison` guard is the only thing that raises). A missing, null or
 * non-string `class` therefore lands in the non-country branch, which is where
 * a road name belongs anyway.
 */
function sovereignLabelSplit(
  countryValue: string,
  otherValue: string,
): StyleExpression {
  const placeClass: StyleExpression = [EXPRESSION_GET, OMT_PLACE_CLASS_FIELD];
  const isNationScale: StyleExpression = [
    EXPRESSION_ANY,
    [EXPRESSION_EQUALS, placeClass, OMT_PLACE_CLASS_COUNTRY],
    [EXPRESSION_EQUALS, placeClass, OMT_PLACE_CLASS_CONTINENT],
  ];

  return [EXPRESSION_CASE, isNationScale, countryValue, otherValue];
}

/** Sovereign labels: 19.6:1 (white branch) and 15.4:1 (accent branch) against
 *  the rgb(12,12,12) basemap — both far past the WCAG AA 4.5:1 floor the
 *  vendor's 3.4:1 grey misses. The split is a hierarchy, never a legibility
 *  trade. */
const LABEL_TEXT_COLOR: StyleExpression = sovereignLabelSplit(
  MAP_ACCENT_COLOR,
  '#ffffff',
);
const LABEL_HALO_COLOR = '#05070a';
const LABEL_HALO_WIDTH = 1.6;

/** Whisper roads: composited over the 0.0037 basemap these sit an order of
 *  magnitude under emitted-light greys, so the network is structure for the
 *  labels and pins to stand on rather than something the eye lands on. One
 *  step lower and it stops resolving at all. */
const ROAD_ARTERIAL_COLOR = '#4a5158';
const ROAD_ARTERIAL_OPACITY = 0.4;
const ROAD_MINOR_COLOR = '#383e44';
const ROAD_MINOR_OPACITY = 0.34;

/** Petrol water: the edge is close to the accent in hue, held at low chroma so
 *  the proximity reads as depth rather than as tint (4.3:1 against the land);
 *  its blue stays ahead of its green on purpose, because water that reads
 *  green reads as a rendering fault. The fill alone only moves the water a few
 *  points away from the land — the outline is what draws the coast. */
const WATER_FILL_COLOR = '#0c2229';
const WATER_EDGE_COLOR = '#3f7f88';

/** Layer ids grouped by the role the classifier assigned them. Returned as its
 *  own value (rather than applied inline) so the classification can be checked
 *  against a style JSON without a map. */
export interface MapCartographyLayers {
  labelLayerIds: string[];
  arterialRoadLayerIds: string[];
  minorRoadLayerIds: string[];
  waterFillLayerIds: string[];
  waterEdgeLayerIds: string[];
}

function sourceLayerOf(layer: StyleLayer): string | null {
  if (!(SOURCE_LAYER_PROPERTY in layer)) {
    return null;
  }

  return layer[SOURCE_LAYER_PROPERTY] ?? null;
}

function filterOf(layer: StyleLayer): unknown {
  return FILTER_PROPERTY in layer ? layer[FILTER_PROPERTY] : null;
}

function matchesIdHint(layerId: string, hints: readonly string[]): boolean {
  const normalizedId = layerId.toLowerCase();

  return hints.some((hint) => normalizedId.includes(hint));
}

/** A symbol layer that carries text is a NAME, whatever surface it annotates —
 *  road names, water names and place names all take the label treatment. */
function isLabelLayer(layer: StyleLayer): boolean {
  return (
    layer.type === LAYER_TYPE_SYMBOL && layer.layout?.[TEXT_FIELD_PROPERTY] != null
  );
}

function isRoadLineLayer(layer: StyleLayer): boolean {
  if (layer.type !== LAYER_TYPE_LINE) {
    return false;
  }

  const sourceLayer = sourceLayerOf(layer);

  return sourceLayer == null
    ? matchesIdHint(layer.id, ROAD_ID_HINTS)
    : sourceLayer === OMT_TRANSPORTATION_SOURCE_LAYER;
}

function isWaterLayer(layer: StyleLayer): boolean {
  const sourceLayer = sourceLayerOf(layer);

  if (sourceLayer == null) {
    return matchesIdHint(layer.id, WATER_ID_HINTS);
  }

  return (
    sourceLayer === OMT_WATER_SOURCE_LAYER ||
    sourceLayer === OMT_WATERWAY_SOURCE_LAYER
  );
}

/** Style filters are arbitrarily nested arrays of expressions; the tokens that
 *  matter (operators and class names) are the string leaves. */
function collectFilterTokens(filter: unknown, tokens: string[]): void {
  if (typeof filter === 'string') {
    tokens.push(filter);
    return;
  }

  if (Array.isArray(filter)) {
    filter.forEach((entry: unknown) => {
      collectFilterTokens(entry, tokens);
    });
  }
}

function isArterialRoadLayer(layer: StyleLayer): boolean {
  const tokens: string[] = [];
  collectFilterTokens(filterOf(layer), tokens);

  if (tokens.some((token) => FILTER_NEGATION_TOKENS.has(token))) {
    return false;
  }

  if (tokens.some((token) => ARTERIAL_ROAD_CLASSES.has(token))) {
    return true;
  }

  // Only reached for a layer that filters on nothing at all — then the id is
  // the sole remaining evidence, and the same class vocabulary is what style
  // authors name those layers after.
  return tokens.length === 0 && matchesIdHint(layer.id, ARTERIAL_ROAD_CLASS_NAMES);
}

/** Roles are exclusive and tested in reading order: a name is a name before it
 *  is a road, and a road is a road before the water fallback ever sees it. */
export function classifyMapCartographyLayers(
  layers: readonly StyleLayer[],
): MapCartographyLayers {
  const classified: MapCartographyLayers = {
    labelLayerIds: [],
    arterialRoadLayerIds: [],
    minorRoadLayerIds: [],
    waterFillLayerIds: [],
    waterEdgeLayerIds: [],
  };

  layers.forEach((layer) => {
    if (isLabelLayer(layer)) {
      classified.labelLayerIds.push(layer.id);
      return;
    }

    if (isRoadLineLayer(layer)) {
      if (isArterialRoadLayer(layer)) {
        classified.arterialRoadLayerIds.push(layer.id);
      } else {
        classified.minorRoadLayerIds.push(layer.id);
      }
      return;
    }

    if (!isWaterLayer(layer)) {
      return;
    }

    if (layer.type === LAYER_TYPE_FILL) {
      classified.waterFillLayerIds.push(layer.id);
      return;
    }

    if (layer.type === LAYER_TYPE_LINE) {
      classified.waterEdgeLayerIds.push(layer.id);
    }
  });

  return classified;
}

/**
 * Every paint write is guarded on its own.
 *
 * A vendor style is free to file a layer type we did not expect under a
 * source-layer we classified — a `line` under `water`, a `fill-extrusion` under
 * `transportation` — and MapLibre throws on a property the layer does not own
 * (it dereferences the layer's own paint-value table). One throw inside the
 * `style.load` handler would take the whole map down with it, so a rejected
 * property is skipped and every other write still lands.
 */
function writePaint<K extends PaintPropertyName>(
  map: MapLibreMapInstance,
  layerId: string,
  property: K,
  value: PaintProperties[K],
): void {
  try {
    map.setPaintProperty(layerId, property, value);
  } catch {
    // Rejected by this layer; the remaining layers and properties are unaffected.
  }
}

/**
 * Nothing to classify until the style has PARSED — the layers do not exist
 * before then, and `getStyle()` answers a style it has not parsed with
 * `undefined`, which dereferencing turns into the throw this catch absorbs.
 *
 * Parsed is the entire question, and `isStyleLoaded()` is NOT the way to ask
 * it. That predicate is `Style.loaded()`, which walks every source's tile
 * manager and stays false until the last tile is in — while `Style._load` sets
 * `_loaded`, creates the layers, and only THEN fires `style.load`. So inside
 * the `style.load` handler this module hangs off, `isStyleLoaded()` is false by
 * construction, and gating on it handed back an empty layer set on every call
 * the baked cartography ever made. `getStyle()` gates on `_loaded` itself,
 * which is exactly the question, so asking it directly is both shorter and
 * correct.
 */
function readStyleLayers(map: MapLibreMapInstance): readonly StyleLayer[] {
  try {
    return map.getStyle().layers;
  } catch {
    return [];
  }
}

/**
 * Writes the baked treatment into the live style.
 *
 * Idempotent and re-runnable: each call re-reads and re-classifies the current
 * style, so a style that parses after the first attempt is restyled in place
 * with no remount and no camera disturbance. Roads and waterways both spell
 * `line-color` but never on the same layer, because classification is
 * exclusive.
 */
export function applyMapCartography(map: MapLibreMapInstance): void {
  const layers = readStyleLayers(map);
  if (layers.length === 0) {
    return;
  }

  const classified = classifyMapCartographyLayers(layers);

  classified.labelLayerIds.forEach((layerId) => {
    writePaint(map, layerId, PAINT_TEXT_COLOR, LABEL_TEXT_COLOR);
    writePaint(map, layerId, PAINT_TEXT_HALO_COLOR, LABEL_HALO_COLOR);
    writePaint(map, layerId, PAINT_TEXT_HALO_WIDTH, LABEL_HALO_WIDTH);
  });

  classified.arterialRoadLayerIds.forEach((layerId) => {
    writePaint(map, layerId, PAINT_LINE_COLOR, ROAD_ARTERIAL_COLOR);
    writePaint(map, layerId, PAINT_LINE_OPACITY, ROAD_ARTERIAL_OPACITY);
  });
  classified.minorRoadLayerIds.forEach((layerId) => {
    writePaint(map, layerId, PAINT_LINE_COLOR, ROAD_MINOR_COLOR);
    writePaint(map, layerId, PAINT_LINE_OPACITY, ROAD_MINOR_OPACITY);
  });

  classified.waterFillLayerIds.forEach((layerId) => {
    writePaint(map, layerId, PAINT_FILL_COLOR, WATER_FILL_COLOR);
    // The outline is drawn by a SECOND pass that MapLibre skips outright when
    // the layer's `fill-antialias` is false (`drawOutline` returns on that
    // check before it reads the colour at all), and OpenFreeMap's `water`
    // layer ships exactly that: `"fill-antialias": false`. Writing the colour
    // without the flag is not half the treatment, it is none of it.
    writePaint(map, layerId, PAINT_FILL_ANTIALIAS, true);
    writePaint(map, layerId, PAINT_FILL_OUTLINE_COLOR, WATER_EDGE_COLOR);
  });
  classified.waterEdgeLayerIds.forEach((layerId) => {
    writePaint(map, layerId, PAINT_LINE_COLOR, WATER_EDGE_COLOR);
  });
}
